import React, { useEffect, useMemo, useState } from "react";
import {
	Alert,
	Checkbox,
	DatePicker,
	Input,
	Modal,
	Select,
	Switch,
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import useLang from "../../../hooks/useLang";
import {
	BulkStatusUpdatePreflight,
	BulkStatusUpdateRequest,
	BulkStatusUpdateTarget,
	ResponsibleUser,
} from "../../../api/bulk-order-operations";
import { HistoryManagementCategorySettings } from "../../../utils/bulkOperationsConfig";
import { PRIMARY_BUTTON_STYLE } from "../components/styles";
import DriverAssignInfoModal from "./DriverAssignInfoModal";
import { getStatusesForDirection } from "./statuses";

/** dbo.StatusHistory.StatusChangeComment is varchar(4000). */
const MAX_COMMENT_LENGTH = 4000;

interface StatusUpdateModalProps {
	open: boolean;
	orderIds: number[];
	preflight: BulkStatusUpdatePreflight;
	users: ResponsibleUser[];
	usersLoading: boolean;
	/** Reused from the History Management block — same system account, one source of truth. */
	historyConfig: HistoryManagementCategorySettings | null;
	onCancel: () => void;
	onSubmit: (body: BulkStatusUpdateRequest) => void;
}

const userLabel = (user: ResponsibleUser) => {
	const name = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
	return user.role ? `${name} (${user.role})` : name;
};

const StatusUpdateModal = ({
	open,
	orderIds,
	preflight,
	users,
	usersLoading,
	historyConfig,
	onCancel,
	onSubmit,
}: StatusUpdateModalProps) => {
	const { strings } = useLang("bulkOrderOperations");
	const copy = strings?.statusUpdates;
	const common = strings?.common;

	const [target, setTarget] = useState<BulkStatusUpdateTarget | null>(null);
	const [userId, setUserId] = useState<number | null>(null);
	const [writeHistory, setWriteHistory] = useState(true);
	// Off by default and never auto-enabled: it overwrites the driver credited with the
	// delivery, which drives settlement.
	const [assignDriver, setAssignDriver] = useState(false);
	const [driverInfoOpen, setDriverInfoOpen] = useState(false);
	const [statusDate, setStatusDate] = useState<Dayjs | null>(dayjs());
	const [message, setMessage] = useState("");

	const statuses = useMemo(
		() => getStatusesForDirection(preflight.direction),
		[preflight.direction]
	);
	const selected = statuses.find((s) => s.target === target) ?? null;

	// For an international batch the responsible user is not the operator's choice: it is
	// this deployment's own Postman system account, from config.
	const effectiveUserId = preflight.isInternational
		? historyConfig?.internationalStatusUserId ?? null
		: userId;

	useEffect(() => {
		if (!open) return;
		setTarget(null);
		setUserId(null);
		setWriteHistory(true);
		setAssignDriver(false);
		setStatusDate(dayjs());
		setMessage("");
	}, [open]);

	// Prefilled from the status but left editable, as asked.
	useEffect(() => {
		if (selected) setMessage(selected.defaultComment);
	}, [selected]);

	// Only meaningful for a local batch, which is the only place the driver info modal shows.
	const selectedUser = users.find((u) => u.id === effectiveUserId) ?? null;
	const selectedUserName = selectedUser
		? `${selectedUser.firstName ?? ""} ${
				selectedUser.lastName ?? ""
		  }`.trim()
		: "";

	const canSubmit =
		!!selected && !!effectiveUserId && message.length <= MAX_COMMENT_LENGTH;

	const handleOk = () => {
		if (!selected || !effectiveUserId) return;
		onSubmit({
			orderIds,
			status: selected.target,
			userId: effectiveUserId,
			writeHistory,
			// Local batches only — the checkbox is not rendered otherwise, and the API
			// rejects the combination anyway.
			assignDeliveryDriver: !preflight.isInternational && assignDriver,
			// Only meaningful when the toggle is on; omitted entirely otherwise so the
			// payload can't imply a history row that was never asked for.
			...(writeHistory
				? {
						// Local wall-clock with NO timezone suffix, deliberately not
						// toISOString(). StatusDate is a plain `datetime` every other
						// writer fills with GETDATE(), so a "…Z" value would be stored
						// shifted and land out of order in the timeline.
						statusDate: statusDate
							? statusDate.format("YYYY-MM-DDTHH:mm:ss")
							: undefined,
						statusChangeComment: message,
				  }
				: {}),
		});
	};

	return (
		<Modal
			open={open}
			title={copy?.modalTitle ?? "Change status"}
			okText={common?.submit ?? "Continue"}
			cancelText={common?.cancel ?? "Cancel"}
			okButtonProps={{
				style: PRIMARY_BUTTON_STYLE,
				disabled: !canSubmit,
			}}
			onOk={handleOk}
			onCancel={onCancel}
			destroyOnClose
		>
			<div className="flex flex-col gap-3 py-2">
				<div>
					<div className="font-medium mb-1">
						{copy?.fieldStatus ?? "Status"}
					</div>
					<Select
						className="w-full"
						value={target}
						onChange={setTarget}
						placeholder={copy?.selectStatus ?? "Select a status"}
						options={statuses.map((s) => ({
							value: s.target,
							label: copy?.[s.labelKey] ?? s.fallbackLabel,
						}))}
					/>
					{selected && (
						<div className="text-xs text-gray-500 mt-1">
							{copy?.willWrite ?? "Orders will be set to"}:{" "}
							<b>{selected.pairs[preflight.direction]}</b>
							{writeHistory && (
								<>
									{" · "}
									{copy?.willWriteHistory ??
										"history row"}:{" "}
									<b>{selected.historyStatusId}</b>
								</>
							)}
						</div>
					)}
				</div>

				<div>
					<div className="font-medium mb-1">
						{copy?.fieldUser ?? "Responsible user"}
					</div>
					<Select
						className="w-full"
						disabled={preflight.isInternational}
						loading={usersLoading}
						value={effectiveUserId}
						onChange={setUserId}
						showSearch
						optionFilterProp="label"
						placeholder={copy?.selectUser ?? "Select a user"}
						options={
							preflight.isInternational
								? [
										{
											value: historyConfig?.internationalStatusUserId,
											label: historyConfig?.internationalStatusUserName,
										},
								  ]
								: users.map((u) => ({
										value: u.id,
										label: userLabel(u),
								  }))
						}
					/>
					{preflight.isInternational && (
						<Alert
							className="mt-2"
							type="info"
							showIcon
							message={
								copy?.internationalUserNote ??
								"International batches are always recorded under this country's Postman system account."
							}
						/>
					)}
				</div>

				{/*
					Delivery-driver reassignment is opt-in and LOCAL ONLY. An international
					batch's responsible user is the Postman system account, which must never
					land in DeliveryDriverId — so the checkbox is not rendered at all rather
					than rendered disabled.
				*/}
				{!preflight.isInternational && (
					<div className="rounded-lg border border-gray-200 p-3">
						<Checkbox
							checked={assignDriver}
							onChange={(e) => setAssignDriver(e.target.checked)}
						>
							{copy?.assignDriver ??
								"Also change the delivery driver"}
						</Checkbox>
						<InfoCircleOutlined
							className="ml-2 text-[#4F16C2] cursor-pointer"
							onClick={() => setDriverInfoOpen(true)}
						/>
						<div className="text-xs text-gray-500 mt-1">
							{assignDriver
								? copy?.assignDriverOn ??
								  "The responsible user replaces the current driver on every updated order"
								: copy?.assignDriverOff ??
								  "Orders keep the driver they already have"}
						</div>
					</div>
				)}

				<div className="flex items-center justify-between rounded-lg border border-gray-200 p-3">
					<div className="flex flex-col">
						<span className="font-medium">
							{copy?.writeHistory ?? "Write a status history row"}
						</span>
						<span className="text-xs text-gray-500">
							{writeHistory
								? copy?.writeHistoryOn ??
								  "Orders and the timeline are both updated"
								: copy?.writeHistoryOff ??
								  "Only the order status changes — the timeline will not show it"}
						</span>
					</div>
					<Switch checked={writeHistory} onChange={setWriteHistory} />
				</div>

				{/* Date and description exist only to fill the history row. */}
				{writeHistory && (
					<>
						<div>
							<div className="font-medium mb-1">
								{copy?.fieldDate ?? "Date"}
							</div>
							<DatePicker
								className="w-full"
								showTime
								value={statusDate}
								onChange={setStatusDate}
							/>
						</div>

						<div>
							<div className="font-medium mb-1">
								{copy?.fieldComment ?? "Description"}
							</div>
							<Input.TextArea
								rows={3}
								value={message}
								onChange={(e) => setMessage(e.target.value)}
								placeholder={
									copy?.commentPlaceholder ??
									"Pre-filled from the status — edit if needed"
								}
							/>
							<div className="text-xs text-gray-500 mt-1">
								{copy?.nameSuffixNote ??
									"The responsible user's name is appended automatically."}
							</div>
						</div>
					</>
				)}
			</div>

			<DriverAssignInfoModal
				open={driverInfoOpen}
				userName={selectedUserName}
				orderCount={orderIds.length}
				onClose={() => setDriverInfoOpen(false)}
			/>
		</Modal>
	);
};

export default StatusUpdateModal;
